import { useState } from "react";
import { FiEye } from "react-icons/fi";
import { MdAddCircle, MdEmail } from "react-icons/md";
import { List } from "../../shared/list/List";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { EmailService } from "../../../services/email/email.service";
import { FornecedorService } from "../../../services/fornecedor/fornecedor.service";
import Alert from "../../shared/alert/Alert";
import { EmailModal } from "../../shared/modal/EmailModal";

export const ListaFornecedor = ({ columns, data, page, setPage, limit, setLimit, total, loading, handleFiltering, getRowStyle, getRowClassName, legend }) => {
  const table = { columns, data };
  const navigate = useNavigate();
  const initialVisibleColumns = ["actions", "codcli", "cgccli", "nomcli", "cadinipfaprov", "datcadinipf", "cadfinpfaprov", "datcadfinpf", "cliafa", "priacpf"];
  const [columnFilters, setColumnFilters] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [emailData, setEmailData] = useState({
    destinatario: [],
    assunto: "",
    conteudo: "",
  });
  const { user } = useAuth();

  const handleVisualizarFornecedor = (row) => navigate(`/fornecedores/${row.codcli}`);

  const getRecipientOptions = (rows = []) => {
    const recipients = new Map();

    rows.forEach((row) => {
      [
        { email: row.endele, tipo: "principal" },
        { email: row.endalt, tipo: "secundario" },
      ].forEach(({ email, tipo }) => {
        if (!email) return;

        recipients.set(email, {
          value: email,
          label: `${row.nomcli || row.codcli} - ${email} (${tipo})`,
          name: row.nomcli || `Fornecedor ${row.codcli}`,
          email,
          type: tipo,
          isPrimary: tipo === "principal",
          shouldSelect: tipo === "principal" || !row.endele,
        });
      });
    });

    return Array.from(recipients.values());
  };

  const resetEmailModal = () => {
    setShowEmailModal(false);
    setEmailRecipients([]);
    setEmailData({ destinatario: [], assunto: "", conteudo: "" });
  };

  const handleEnviarEmail = async (rows = []) => {
    const recipients = getRecipientOptions(rows);

    if (!recipients.length) {
      await Alert({
        icon: "warning",
        title: "Atencao",
        message: "Nenhum fornecedor selecionado possui email cadastrado.",
        showConfirmButton: true,
        confirmButtonText: "OK",
      });
      return;
    }

    setEmailRecipients(recipients);
    setEmailData({
      destinatario: recipients
        .filter((recipient) => recipient.shouldSelect)
        .map((recipient) => recipient.value),
      assunto: "",
      conteudo: "",
    });
    setShowEmailModal(true);
  };

  const handleConfirmarEnvioEmail = async () => {
    const destinatarios = Array.isArray(emailData.destinatario)
      ? emailData.destinatario
      : [emailData.destinatario].filter(Boolean);

    if (!destinatarios.length || !emailData.assunto.trim() || !emailData.conteudo.trim()) {
      await Alert({
        icon: "warning",
        title: "Atenção",
        message: "Selecione ao menos um destinatário e preencha o assunto e o conteúdo do email.",
        showConfirmButton: true,
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      setEmailLoading(true);
      await Promise.all(
        destinatarios.map((destinatario) =>
          EmailService.enviarEmail({
            destinatario,
            assunto: emailData.assunto,
            conteudo: emailData.conteudo,
          })
        )
      );

      resetEmailModal();
      await Alert({
        icon: "success",
        title: "Sucesso",
        message: "Email enviado para os fornecedores selecionados com sucesso!",
        showConfirmButton: true,
        confirmButtonText: "OK",
      });
    } catch (error) {
      const message = error.response?.data?.erro || error.message;
      await Alert({
        icon: "error",
        title: "Erro",
        message: `Erro ao enviar email: ${message}`,
        showConfirmButton: true,
        confirmButtonText: "OK",
      });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleCriarRegistroAvaliacao = async (rows = []) => {
    if (!rows.length) return;

    let message =
      rows.length > 1
        ? "Confirma a inclusão de um novo Registro de Avaliação para esses fornecedores?"
        : "Confirma a inclusão de um novo Registro de Avaliação para esse fornecedor?";

    const confirmed = await Alert({
      title: "Nova Avaliação",
      message,
      icon: "info",
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
    });

    if (!confirmed) return;

    try {
      const codcli = rows.map(({ codcli }) => codcli);
      await FornecedorService.postAvaliacaoEmLote(
        user.token,
        { codcli, codope: user.codope }
      );

      message =
        rows.length > 1
          ? "Registros de avaliação criados com sucesso!"
          : "Registro de avaliação criado com sucesso!";

      Alert({
        icon: "success",
        title: "Sucesso",
        message,
        showConfirmButton: true,
        confirmButtonText: "OK",
      });
    } catch (error) {
      message = error.response?.data?.erro || error.message;
      await Alert({
        icon: "error",
        title: "Erro",
        message: `Erro ao criar a avaliação: ${message}`,
        showConfirmButton: true,
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <>
      {showEmailModal && (
        <EmailModal
          show={showEmailModal}
          recipientOptions={emailRecipients}
          multipleRecipients
          emailData={emailData}
          setEmailData={setEmailData}
          onClose={resetEmailModal}
          onSend={handleConfirmarEnvioEmail}
          isLoading={emailLoading}
        />
      )}

      <List
        title="Fornecedores"
        text="Lista de Fornecedores"
        table={table}
        actions={[{ icon: FiEye, onClick: handleVisualizarFornecedor, title: "Visualizar Fornecedor" }]}
        bulkActions={[{ icon: MdAddCircle, label: "Criar Avaliação", onClick: handleCriarRegistroAvaliacao }, { icon: MdEmail, label: "Enviar Email", onClick: handleEnviarEmail },]}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
        total={total}
        onColumnFilterChange={handleFiltering}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        initialVisibleColumns={initialVisibleColumns}
        loading={loading}
        getRowStyle={getRowStyle}
        getRowClassName={getRowClassName}
        legend={legend}
      />
    </>
  );
};
